import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Divider from '../../components/divider/divider.component';
import ProjectModal from './parts/project-modal.component';
import ProjectHistoryModal from './parts/project-history-modal.component';
import Table from '../../components/table/table.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import { setCurrentPage } from '../../redux/entities/admin/projects';
import { getProjects, deleteProjects } from '../../redux/thunks/admin/projects';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { RowButtons } from '../../components/table/table.styles';
import { FaEye } from 'react-icons/fa';
import { statusOptions } from '../../utils/form-configs/projects.form';

const ProjectsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { projects, currentPage, limit, totalDocs, isUpdating, isFetching } = useSelector(state => state.entities.admin.projects);
  const [showAddModal, setShowAddModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projectHistory, setProjectHistory] = useState(null);

  useEffect(() => {
    dispatch(getProjects({
      page: currentPage,
      limit,
      search: searchValue
    }));
  }, [currentPage, limit, searchValue]);

  useDebounce(() => {
    dispatch(setCurrentPage(1));
  }, [searchValue], 500);

  const handleDelete = (ids) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      setProjectToDelete(null);
    };
    const onFailure = (message) => enqueueSnackbar(message, { variant: 'error' });

    dispatch(deleteProjects({ ids, onSuccess, onFailure }));
  };

  const getLeftHoursBadge = (totalMinutes, leftMinutes) => {
    const percentage = (leftMinutes / totalMinutes) * 100;
    
    if (percentage < 0) {
      return {
        leftHoursBadgeBg: 'danger',
        leftHoursBadgeStyles: {}
      };
    }

    if (percentage <= 10) {
      return {
        leftHoursBadgeBg: '',
        leftHoursBadgeStyles: {
          backgroundColor: '#e17800',
        }
      };
    }

    if (percentage <= 20) {
      return {
        leftHoursBadgeBg: 'warning',
        leftHoursBadgeStyles: {}
      };
    }

    return {
      leftHoursBadgeBg: 'dark',
      leftHoursBadgeStyles: {}
    };
  };

  const getProjectStatusBadge = (status) => {
    const projectStatusOption = statusOptions.find(option => option.value === status);
    const projectStatusLabel = projectStatusOption ? projectStatusOption.label : 'Unknown';

    let bg = '';
    let style = {};

    switch (status) {
      case 'active':
        bg = 'primary';
        break;
      case 'pending':
        bg = 'warning';
        style = { color: '#000' };
        break;
      case 'completed':
        bg = 'dark';
        break;
      default:
        bg = 'dark';
        break;
    }

    return <Badge bg={bg} style={style}>{projectStatusLabel}</Badge>;
  };

  const handleEdge = useCallback(() => {
    if (currentPage * limit < totalDocs) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  }, [currentPage, limit, totalDocs]);

  return <>
    {showAddModal && <ProjectModal
      show={showAddModal}
      onClose={() => setShowAddModal(false)}
    />}
    
    {projectToEdit && <ProjectModal
      show={projectToEdit}
      onClose={() => setProjectToEdit(null)}
      project={projectToEdit}
    />}

    {projectHistory && <ProjectHistoryModal
      show={projectHistory}
      onClose={() => setProjectHistory(null)}
      history={projectHistory}
    />}

    <ConfirmModal
      show={projectToDelete} 
      onClose={() => setProjectToDelete(null)}
      onConfirm={() => handleDelete([projectToDelete])}  
    />

    <Table
      title="Projects"
      showAmount={true}
      amount={totalDocs}
      headerButtons={<>
        <Button variant="primary" onClick={() => setShowAddModal(true)} style={{ width: '-webkit-fill-available' }}>Add Project</Button>
      </>}
      headerItems={[
        { label: 'Name' },
        { label: 'Status' },
        { label: 'Paid Amount' },
        { label: 'Paid Hours' },
        { label: 'Left Hours' },
        { label: 'Employees' },
        { label: 'History' },
        { label: '' },
      ]}
      onSearch={setSearchValue}
      onEdge={handleEdge}
      isLoading={isFetching}
    >
      {projects.map((project, index) => {
        const leftHours = parseFloat(Number(project.leftMinutes) / 60).toFixed(2);
        const { leftHoursBadgeBg, leftHoursBadgeStyles } = getLeftHoursBadge(project.totalMinutes, project.leftMinutes);

        return <>
          <div key={index}>
            <span>{project.name}</span>
            <span>{getProjectStatusBadge(project.status)}</span>
            <span><Badge bg={'success'}>{project.paidAmount}</Badge></span>
            <span><Badge bg={'dark'}>{project.paidHours}</Badge></span>
            <span><Badge bg={leftHoursBadgeBg} style={{ ...leftHoursBadgeStyles }}>{leftHours}</Badge></span>
            <span><Badge bg={project.users.length > 0 ? 'primary' : 'danger'}>{project.users.length}</Badge></span>
            <span>
              {project.history && (
                <span style={{ cursor: 'pointer' }} onClick={() => setProjectHistory(project.history)}>
                <FaEye color="#17a9d1" fontSize={'1.2rem'} />
              </span>
              )}
            </span>
            <RowButtons>
              <Button variant="primary" size='sm' onClick={() => setProjectToEdit(project)} disabled={isUpdating[project._id]}>Edit</Button>
              <Button variant="danger" size='sm' onClick={() => setProjectToDelete(project._id)} disabled={isUpdating[project._id]}>Delete</Button>
            </RowButtons>
          </div>
          <Divider />
        </>;
      })}
    </Table>
  </>;
};

export default ProjectsPage;
